/**
 * Email Triggers Type Definitions
 * Matches SMS triggers structure but for email channel
 */

// ============================================================================
// Core Types
// ============================================================================

export type TriggerType = 'milestone' | 're_engagement';
export type TriggerFormStep = 'basic' | 'conditions' | 'message' | 'timing' | 'review';
export type TriggerStatus = 'active' | 'paused' | 'draft' | 'expired';
export type ExecutionStatus = 'pending' | 'scheduled' | 'sent' | 'failed' | 'cancelled';

// Condition-related types
export type ConditionField =
  | 'total_orders'
  | 'total_spend'
  | 'average_order_value'
  | 'days_since_last_order'
  | 'service_type'
  | 'loyalty_tier';

export type ConditionOperator =
  | 'equals'
  | 'not_equals'
  | 'greater_than'
  | 'less_than'
  | 'greater_than_or_equal'
  | 'less_than_or_equal'
  | 'between'
  | 'in'
  | 'not_in'
  | 'contains'
  | 'not_contains';

export type LogicOperator = 'AND' | 'OR';
export type TimeUnit = 'minutes' | 'hours' | 'days' | 'weeks';

// ============================================================================
// Interface Definitions
// ============================================================================

export interface TimeDelay {
  value: number;
  unit: TimeUnit;
}

export interface TriggerConditionFormData {
  id: string;
  field: ConditionField;
  operator: ConditionOperator;
  value: any;
  field_type: 'number' | 'string' | 'array';
  isRequired?: boolean;
}

/**
 * Email Trigger Template interface (reference-based)
 * Templates store metadata only, email content comes from system templates
 */
export interface EmailTriggerTemplate {
  id: string;
  name: string;
  description: string;
  category: 'welcome' | 'review' | 'loyalty' | 'retention' | 'milestone';
  trigger_type: TriggerType;

  // Conditions
  default_conditions: TriggerConditionFormData[];
  default_logic: LogicOperator;
  default_time_delay: TimeDelay;

  // System template reference (email content stored separately)
  system_template_id: string | null;
  system_template_name: string;

  // Shared fields
  personalization_fields: string[];
  default_priority: number;
  default_max_sends_per_customer?: number;
  default_cooldown_days?: number;

  use_cases: string[];
}

/**
 * Email Trigger Rule (database record)
 */
export interface EmailTriggerRule {
  id: string;
  organisation_id: string;
  rule_name: string;
  rule_description?: string;
  trigger_type: TriggerType;

  rule_conditions: {
    logic: LogicOperator;
    conditions: Record<string, any>;
  };

  time_delay: TimeDelay;

  // Email-specific fields
  email_subject: string;
  email_content_html: string;
  email_content_text: string;
  email_design_json?: any;
  email_domain_id: string;
  sender_email: string;
  sender_name: string;

  priority: number;
  max_sends_per_customer?: number;
  cooldown_days?: number;
  is_active: boolean;

  starts_at?: string;
  expires_at?: string;

  created_at: string;
  updated_at: string;
  created_by?: string;
}

/**
 * Form data for creating/editing email triggers
 */
export interface EmailTriggerFormData {
  rule_name: string;
  rule_description: string;
  trigger_type: TriggerType;
  conditions: TriggerConditionFormData[];
  logic: LogicOperator;
  time_delay: TimeDelay;

  // Email-specific fields
  email_subject: string;
  email_content_html: string;
  email_content_text: string;
  email_design_json?: any;
  email_domain_id: string;
  email_prefix: string;
  sender_name: string;

  personalization_fields?: string[];
  priority: number;
  max_sends_per_customer?: number;
  cooldown_days?: number;
  starts_at?: string;
  expires_at?: string;
  is_active: boolean;
}

// ============================================================================
// Labels & Display Constants
// ============================================================================

export const TRIGGER_TYPE_LABELS: Record<TriggerType, string> = {
  milestone: 'Customer Milestone',
  re_engagement: 'Re-engagement'
};

export const TRIGGER_TYPE_DESCRIPTIONS: Record<TriggerType, string> = {
  milestone: 'Send emails based on customer activity and milestones (e.g., first order, nth order, spending thresholds)',
  re_engagement: 'Send emails to customers who haven\'t ordered in a specified time period'
};

export const TRIGGER_STATUS_LABELS: Record<TriggerStatus, string> = {
  active: 'Active',
  paused: 'Paused',
  draft: 'Draft',
  expired: 'Expired'
};

export const EXECUTION_STATUS_LABELS: Record<ExecutionStatus, string> = {
  pending: 'Pending',
  scheduled: 'Scheduled',
  sent: 'Sent',
  failed: 'Failed',
  cancelled: 'Cancelled'
};

export const CONDITION_FIELD_LABELS: Record<ConditionField, string> = {
  total_orders: 'Total Orders',
  total_spend: 'Total Spend',
  average_order_value: 'Average Order Value',
  days_since_last_order: 'Days Since Last Order',
  service_type: 'Service Type',
  loyalty_tier: 'Loyalty Tier'
};

export const OPERATOR_LABELS: Record<ConditionOperator, string> = {
  equals: 'Equals',
  not_equals: 'Not Equals',
  greater_than: 'Greater Than',
  less_than: 'Less Than',
  greater_than_or_equal: 'Greater Than or Equal',
  less_than_or_equal: 'Less Than or Equal',
  between: 'Between',
  in: 'In',
  not_in: 'Not In',
  contains: 'Contains',
  not_contains: 'Does Not Contain'
};

export const TIME_UNIT_LABELS: Record<TimeUnit, string> = {
  minutes: 'Minutes',
  hours: 'Hours',
  days: 'Days',
  weeks: 'Weeks'
};

// ============================================================================
// Hook Return Types
// ============================================================================

export interface UseTriggerTemplates {
  templates: EmailTriggerTemplate[];
  loading: boolean;
}
